import React, { useState, useEffect } from "react";
import { Button, Select, SelectItem, Modal } from "carbon-components-react";
import { Filter16 } from "@carbon/icons-react";

import { ColumnDef, ColumnKind, HydratedFilterAction, Schema } from "../shared";
import { getColumnDefByJsonKey } from "../helpers";
import { Operator, operatorToDisplayString, kindToOperators } from "../operators";

import getFilterAction from "./actions";
import ComparisonValueInput from "./ComparisonValueInput";

const styles = require("./styles.module.scss");

const columnOptionsFromSchema = (s: Schema): { label: string; value: string }[] =>
  s.map((d: ColumnDef) => ({ value: d.jsonKey, label: d.displayName }));

interface Props {
  schema: Schema;
  addAction: Function;

  actionToUpdate: HydratedFilterAction;
  updateAction: Function;
  openModal: boolean;
}

const AddFilter: React.FC<Props> = ({
  schema,
  addAction,
  actionToUpdate,
  updateAction = null,
  openModal = false,
}) => {
  const [modalIsOpen, setModalOpen] = useState(openModal);

  // Powers the key,operator, and comparisonValue selection logic
  const [selectedKey, setSelectedKey] = useState("");
  const [selectedOperator, setSelectedOperator] = useState<Operator | string>("");
  const [selectedComparisonValue, setSelectedComparisonValue] = useState("");
  const [selectedComparisonList, setSelectedComparisonList] = useState([]);

  const currentColumnDef = selectedKey !== "" ? getColumnDefByJsonKey(schema, selectedKey) : null;

  const columnKindsThatDoNotNeedComparisonValue = [ColumnKind.Boolean];
  const operatorsThatDoNotNeedComparisonValue = [
    Operator.IsEmpty,
    Operator.IsNotEmpty,
    Operator.CurrentDay,
    Operator.LastSevenDays,
    Operator.LastThirtyDays,
    Operator.LastThreeMonths,
    Operator.LastSixMonths,
    Operator.LastTwelveMonths,
    Operator.NextSevenDays,
    Operator.NextThirtyDays,
    Operator.NextThreeMonths,
  ];
  const columnKindsThatRequireListComparisons = [ColumnKind.Date, ColumnKind.Time, ColumnKind.Enum];

  const reset = () => {
    setSelectedKey("");
    setSelectedOperator("");
    setSelectedComparisonValue("");
    setSelectedComparisonList([]);

    setModalOpen(false);
  };

  // Sets hooks based on whether we're SAVING or UPDATING a filter action
  useEffect(() => {
    if (!actionToUpdate) {
      return;
    }

    setModalOpen(openModal);
    setSelectedKey(actionToUpdate.jsonKey);
    setSelectedOperator(actionToUpdate.operator);

    const def = getColumnDefByJsonKey(schema, actionToUpdate.jsonKey);

    // Comparison values vary based on ColumnKind, so ensure we update the correct hook
    if (actionToUpdate && columnKindsThatRequireListComparisons.includes(def.kind)) {
      setSelectedComparisonList(actionToUpdate.comparisonValue);
    } else {
      setSelectedComparisonValue(actionToUpdate.comparisonValue);
    }
  }, [actionToUpdate]);

  return (
    <div>
      <Modal
        open={modalIsOpen}
        modalHeading="Add filter"
        primaryButtonText="Apply filter"
        secondaryButtonText="Cancel"
        onBlur={() => reset()}
        onRequestClose={() => reset()}
        onRequestSubmit={() => {
          const newAction = getFilterAction(
            schema,
            selectedKey,
            selectedOperator as Operator,
            selectedComparisonValue || selectedComparisonList,
          );

          if (actionToUpdate) {
            updateAction(actionToUpdate.idx, newAction);
            reset();
            return;
          }

          addAction(newAction);
          reset();
        }}
        // NOTE: Keep `hasScrollingContent` and style to allow MultiSelect's Dropdown to be visible
        hasForm
        aria-label="Add filter modal"
        modalAriaLabel="Add filter modal"
        hasScrollingContent
        className={styles.addFilterModal}
      >
        <div
          style={{
            display: "flex",
            flexFlow: "row nowrap",
          }}
        >
          <div style={{ width: "50%" }}>
            {/* TODO KEVIN Update to ComboBox later */}
            <Select
              id="select-filter"
              name="filterKey"
              value={selectedKey}
              labelText="Select a field to filter by"
              onChange={e => {
                setSelectedKey(e.target.value);

                // When changing the field, we want to wipe the current operator state
                setSelectedOperator("");
                setSelectedComparisonValue("");
                setSelectedComparisonList([]);
              }}
              invalid={false}
              style={{ width: "95%" }}
            >
              <SelectItem text="Select a field to filter by" value="" />
              {columnOptionsFromSchema(schema).map(col => (
                <SelectItem key={col.value} text={col.label} value={col.value} />
              ))}
            </Select>
          </div>
          <div style={{ display: "flex", flexFlow: "column nowrap", width: "50%" }}>
            <Select
              id="select-operator"
              name="operator"
              value={selectedOperator || ""}
              labelText="Operator"
              onChange={e => {
                setSelectedOperator(e.target.value);
              }}
              disabled={selectedKey === ""}
            >
              <SelectItem text="Select an operator" value="" />
              {selectedKey !== "" &&
                kindToOperators(currentColumnDef.kind).map((operator: Operator) => {
                  return (
                    <SelectItem
                      key={operator}
                      text={operatorToDisplayString[operator]}
                      value={operator}
                    />
                  );
                })}
            </Select>

            {/* For most operators, we need a value to compare against */}
            {currentColumnDef &&
              !columnKindsThatDoNotNeedComparisonValue.includes(
                currentColumnDef.kind as ColumnKind,
              ) &&
              !operatorsThatDoNotNeedComparisonValue.includes(selectedOperator as Operator) && (
                <>
                  <hr style={{ marginTop: "1rem" }} />
                  <ComparisonValueInput
                    disabled={selectedOperator === ""}
                    operator={selectedOperator as Operator}
                    columnDef={currentColumnDef}
                    selectedValue={selectedComparisonValue}
                    setValue={setSelectedComparisonValue}
                    selectedList={selectedComparisonList}
                    setListValue={setSelectedComparisonList}
                  />
                </>
              )}
          </div>
        </div>
      </Modal>

      {/* TODO Update the button style to match mock-up once ActionsList stuff matures */}
      <Button
        kind="ghost"
        onClick={() => setModalOpen(true)}
        renderIcon={Filter16}
        iconDescription="Add filter"
      >
        Add Filter
      </Button>
    </div>
  );
};

export default AddFilter;
